/**
 * BrowserStorageSanitizer.ts
 * Enterprise-grade storage teardown utility for guaranteed session isolation.
 * 
 * Follows SECURITY FIRST principles to ensure strict cross-tenant isolation.
 */
import logger from './loggerService';
import { STORAGE_SANITIZER_WHITELIST_KEYS, STORAGE_SANITIZER_WHITELIST_PREFIXES } from '../constants/securityConstants';

export class BrowserStorageSanitizer {

  /**
   * Forcibly purges all data from localStorage and sessionStorage
   * that is not explicitly whitelisted.
   * 
   * @security Prevents cross-tenant data caching vulnerabilities.
   */
  public static purgeAll(): void {
    try {
      this.sanitizeStorage(window.localStorage, 'localStorage');
      this.sanitizeStorage(window.sessionStorage, 'sessionStorage');
      logger.info('Storage sanitized successfully.', { context: 'BrowserStorageSanitizer' });
    } catch (error) {
      logger.error('Failed to sanitize browser storage via selective purge. Forcing absolute clear.', error, 'BrowserStorageSanitizer');
      // If selective purge fails for any reason, execute full fallback purge to guarantee security
      window.localStorage.clear();
      window.sessionStorage.clear();
    }
  }

  private static sanitizeStorage(storage: Storage, storageName: string): void {
    const keysToKeep = new Map<string, string>();
    
    // 1. Identify and extract whitelisted keys BEFORE purging
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key && this.isWhitelisted(key)) {
        // ENTERPRISE HARDENING: Neutralize potential XSS inside whitelisted slots
        const rawValue = storage.getItem(key) || '';
        keysToKeep.set(key, this.sanitizeValue(rawValue));
      }
    }

    // 2. Clear the entire storage
    storage.clear();

    // 3. Re-add only the sanitized, whitelisted keys
    keysToKeep.forEach((value, key) => {
      storage.setItem(key, value);
    });

    if (keysToKeep.size > 0) {
        logger.debug(`Sanitized and retained ${keysToKeep.size} whitelisted keys in ${storageName}.`, { context: 'BrowserStorageSanitizer' });
    } else {
        logger.debug(`No whitelisted keys found or retained in ${storageName}.`, { context: 'BrowserStorageSanitizer' });
    }
  }

  /**
   * Enterprise Hardening: Neutralizes potential HTML/XSS payloads that may have
   * survived inside nominally whitelisted keys (like user theme preferences).
   * Strips out structural HTML tags while perfectly preserving raw text or standard JSON values.
   */
  private static sanitizeValue(value: string): string {
    if (!value) return '';
    return value.replace(/<[^>]*>?/gm, '');
  }

  private static isWhitelisted(key: string): boolean {
    if ((STORAGE_SANITIZER_WHITELIST_KEYS as readonly string[]).includes(key)) {
      return true;
    }
    return STORAGE_SANITIZER_WHITELIST_PREFIXES.some(prefix => key.startsWith(prefix));
  }
}
